import './EventBooking.css'
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useSelectedItem } from "../context/store";
import iziToast from 'izitoast';
import { FaLocationDot } from "react-icons/fa6";
import { SlCalender } from "react-icons/sl";
import 'izitoast/dist/css/iziToast.min.css';
import { FaDirections } from "react-icons/fa";
import EventsListingCard from './EventsListingCard';
import { set } from 'date-fns';
import { fi } from 'date-fns/locale';
import { API_BASE_URL } from '../utils';

const Skeleton = ({ width, height, borderRadius = '4px' }) => {
    return (
        <div
            className="event-booking-skeleton-shimmer"
            style={{
                width,
                height,
                borderRadius
            }}
        ></div>
    );
};


export default function ChooseTicket() {
    const { selectedItem, handleItemClick } = useSelectedItem();
    const params = useParams();
    const navigate = useNavigate();

    // State management
    const [eventdetailsTicket, setEventdetailsTicket] = useState([]);
    const [eventdetails1, setEventdetails1] = useState({});
    const [ticketCounts, setTicketCounts] = useState({});
    const [eventsData, setEventData] = useState([]);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Constants
    const url = `${API_BASE_URL}/master/assets/uploads/`;
    const maxFreeTickets = 10;

    // Utility function to format Indian currency
    const formatIndianCurrency = useCallback((amount) => {
        if (amount === 0) return "₹0";

        const numStr = Math.round(amount).toString();
        const lastThree = numStr.substring(numStr.length - 3);
        const otherNumbers = numStr.substring(0, numStr.length - 3);

        if (otherNumbers !== '') {
            return `₹${otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",")}${lastThree}`;
        } else {
            return `₹${lastThree}`;
        }
    }, []);

    // Check if tickets are free
    const isFreeTiket = useMemo(() => {
        return eventdetailsTicket.length > 0 && eventdetailsTicket[0]?.ticket_type === "Free";
    }, [eventdetailsTicket]);

    // Calculate totals
    const calculations = useMemo(() => {
        const ticketTotal = eventdetailsTicket.reduce((total, ticket) => {
            const count = ticketCounts[ticket.id] || 0;
            return total + (parseFloat(ticket.price) || 0) * count;
        }, 0);

        const totalQty = Object.values(ticketCounts).reduce((sum, count) => sum + count, 0);

        return {
            ticketTotal,
            totalQty,
            isFreeTiket,
            maxTicketsReached: isFreeTiket && totalQty >= maxFreeTickets
        };
    }, [eventdetailsTicket, ticketCounts, isFreeTiket]);

    // Handle ticket count change
    const handleTicketCountChange = useCallback((id, count) => {
        setTicketCounts((prevCounts) => {
            const newCount = Math.max(count, 0);

            // For free tickets, limit to maximum allowed
            if (isFreeTiket) {
                const currentTotal = Object.values(prevCounts).reduce((sum, c) => sum + c, 0);
                const otherTicketsCount = Object.entries(prevCounts)
                    .filter(([ticketId]) => ticketId !== id.toString())
                    .reduce((sum, [, c]) => sum + c, 0);

                const maxAllowedForThisTicket = maxFreeTickets - otherTicketsCount;
                const limitedCount = Math.min(newCount, maxAllowedForThisTicket);

                if (limitedCount !== newCount && newCount > 0) {
                    iziToast.warning({
                        title: 'Ticket Limit Reached',
                        message: `Maximum ${maxFreeTickets} free tickets allowed per booking`,
                        timeout: 3000,
                        position: 'topCenter',
                    });
                }

                const updatedCounts = { ...prevCounts };
                if (limitedCount === 0) {
                    delete updatedCounts[id];
                } else {
                    updatedCounts[id] = limitedCount;
                }

                return updatedCounts;
            }

            // For paid tickets, no limit
            const updatedCounts = { ...prevCounts };
            if (newCount === 0) {
                delete updatedCounts[id];
            } else {
                updatedCounts[id] = newCount;
            }

            return updatedCounts;
        });
    }, [isFreeTiket]);

    // Handle item click
    const handleItemClickEnhanced = useCallback((ticket) => {
        handleItemClick(ticket);

        // Check if adding one more ticket would exceed the limit for free tickets
        if (isFreeTiket) {
            const currentTotal = Object.values(ticketCounts).reduce((sum, count) => sum + count, 0);
            if (currentTotal >= maxFreeTickets) {
                iziToast.warning({
                    title: 'Ticket Limit Reached',
                    message: `Maximum ${maxFreeTickets} free tickets allowed per booking`,
                    timeout: 3000,
                    position: 'topCenter',
                });
                return;
            }
        }

        setTicketCounts((prevCounts) => ({
            ...prevCounts,
            [ticket.id]: (prevCounts[ticket.id] || 0) + 1,
        }));
    }, [handleItemClick, isFreeTiket, ticketCounts]);

    // Handle proceed to checkout
    const handleProceedToCheckout = useCallback(() => {
        const selectedTickets = eventdetailsTicket.filter(ticket => ticketCounts[ticket.id] > 0);

        if (selectedTickets.length === 0) {
            iziToast.warning({
                title: 'No Tickets Selected',
                message: 'Please select at least one ticket to proceed',
                timeout: 3000,
                position: 'topCenter',
            });
            return;
        }

        // Store selected tickets and event details in localStorage or context
        const checkoutData = {
            eventDetails: eventdetails1,
            selectedTickets: selectedTickets.map(ticket => ({
                ...ticket,
                quantity: ticketCounts[ticket.id]
            })),
            ticketCounts,
            isFreeTiket,
            eventId: params.id
        };

        localStorage.setItem('checkoutData', JSON.stringify(checkoutData));
        navigate('/checkout-event');
    }, [eventdetailsTicket, ticketCounts, eventdetails1, isFreeTiket, params.id, navigate]);

    // Fetch event data
    useEffect(() => {
        if (!params.id) return;

        const fetchEventData = async () => {

            setIsLoading(true);
            try {
                const formData = new FormData();
                formData.append("event_id", params.id);

                // Fetch tickets
                const ticketsResponse = await axios.post(
                    `${API_BASE_URL}/master/Api/getDataPost/eventTicketType`,
                    formData
                );

                if (ticketsResponse.data.status !== "true") {
                    setEventdetailsTicket(ticketsResponse.data.data || []);
                }

                // Fetch event details
                const detailsResponse = await axios.post(
                    `${API_BASE_URL}/master/Api/getDataPost/eventDetails`,
                    formData
                );

                if (detailsResponse.data.data?.[0]) {
                    setEventdetails1(detailsResponse.data.data[0]);
                }
            } catch (error) {
                console.error("Error fetching event data:", error);
                iziToast.error({
                    title: 'Error',
                    message: 'Failed to load event details',
                    timeout: 3000,
                    position: 'topCenter',
                });
            }
            finally {
                setIsLoading(false);
            }
        };

        fetchEventData();
    }, [params.id]);

    const openInGoogleMaps = (address) => {
        const encodedAddress = encodeURIComponent(address);
        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
        window.open(mapsUrl, '_blank');
    };

    const checkDateExpire = () => {
        const eventDateStr = eventdetails1?.event_date;
        if (!eventDateStr) return false;

        const dateParts = eventDateStr.split(',')[0].trim().split(' ');
        const year = eventDateStr.split(',')[1]?.trim();

        if (dateParts.length !== 2 || !year) return false;

        const [dayStr, monthStr] = dateParts;
        const day = parseInt(dayStr);

        const monthMap = {
            Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
            Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
        };

        const month = monthMap[monthStr];
        if (month === undefined || isNaN(day) || isNaN(parseInt(year))) return false;

        const eventDate = new Date(parseInt(year), month, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return eventDate < today;
    };

    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'long' });
        const year = date.getFullYear();

        const getOrdinal = (n) => {
            if (n > 3 && n < 21) return 'th';
            switch (n % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        return `${day}${getOrdinal(day)}, ${month} ${year}`;
    };

    const fetchUpcomingEvents = async (lat, lng, date) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('latitude', lat);
            formData.append('longitude', lng);
            formData.append('date', date);

            const response = await axios.post(
                `${API_BASE_URL}/master/APIs/ClubEvent/upcomingEvents`,
                formData
            );

            if (response.data.status === true) {
                setEventData(response.data.data);
            } else {
                setEventData([]);
            }
        } catch (error) {
            console.error('Error fetching upcoming events:', error);
            setEventData([]);
        }
        finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const latStr = latitude.toString();
                    const lngStr = longitude.toString();

                    setLatitude(latStr);
                    setLongitude(lngStr);

                    fetchUpcomingEvents(latStr, lngStr, today);
                },
                (error) => {
                    // alert('Please enable Geo Location Service to find events in your locality.');
                    console.error('Error in finding location:', error);
                }
            );
        } else {
            console.error('Geolocation is not available in this browser.');
        }
    }, []);

    function generateClubUrl(clubName, cityName, areaName, title) {
        const format = (str) => (str ? str.trim().replace(/\s+/g, "_").toLowerCase() : "");
        return `/event/${format(clubName)}-${format(cityName)}-${format(areaName)}/${format(title)}`;
    }

    // Conditional rendering based on expiration
    if (checkDateExpire()) {
        return (
            <div className="choose-ticket-page">
                <div className="choose-ticket-container">
                    <div className="choose-ticket-wrapper">
                        <div className="choose-ticket-expired">
                            <h4>Event Has Been Expired</h4>
                            <h1>Explore Other Events</h1>
                        </div>
                    </div>
                </div>
                <div className="event-cards-container">
                    <div className="event-happening-title">
                        <h1>Events happening on {formatDate(today)}</h1>
                        <Link to='/events'>
                            <p>view all</p>
                        </Link>
                    </div>
                    <div className="event-cards-thank-you-page">
                        {eventsData.slice(0, 3).map((event, index) => (
                            <Link to={generateClubUrl(event.clubName, event.cityName, event.areaName, event.name)} key={index} >
                                <EventsListingCard
                                    title={event.name}
                                    date={event.event_date}
                                    time={event.event_time}
                                    rating={event.rating}
                                    location={event.cityName}
                                    distance={event.distance}
                                    type={event.categoryName}
                                    price={event.min_price}
                                    image={url + event.image}
                                    offer={event.offer}
                                />
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="choose-ticket-page">
            <div className="choose-ticket-container">
                <div className='choose-ticket-wrapper'>
                    <div className="choose-ticket-content">
                        <div className='choose-ticket-content-heading'>
                            <h2>Choose Your Tickets</h2>
                            <span>
                                <Link to={generateClubUrl(eventdetails1.clubName, eventdetails1.clubCity, eventdetails1.clubArea, eventdetails1.name)}>
                                    View Details
                                </Link>
                            </span>
                        </div>

                        <div className="choose-ticket-event-info">

                            {isLoading || !eventdetails1 ? (
                                <div className="choose-ticket-event-name-wrapper">
                                    <div className="choose-ticket-event-image">
                                        <Skeleton width="120px" height="200px" borderRadius="8px" />
                                    </div>
                                    <div style={{width: "100%"}}>
                                        <h4>
                                            <Skeleton width="100%" height="40px" />
                                        </h4>
                                        <div className="choose-ticket-event-details">
                                            <Skeleton width="250px" height="16px" />
                                            <Skeleton width="180px" height="16px" />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className='choose-ticket-event-name-wrapper'>
                                    <div className='choose-ticket-event-image'>
                                        <img src={url + eventdetails1.image} alt="" />
                                    </div>
                                    <div>
                                        <h4>
                                            {eventdetails1.name}
                                            {eventdetails1.categoryName && (
                                                <span> &nbsp; &nbsp;({eventdetails1.categoryName})</span>
                                            )}
                                        </h4>
                                        <p>{eventdetails1.clubName}</p>

                                        <div className='choose-ticket-event-details'>
                                            <p className="choose-ticket-event-address">
                                                <FaLocationDot />
                                                {eventdetails1?.clubArea} {","} {eventdetails1?.clubCity}  {eventdetails1?.clubAddress && (
                                                    <div className='choose-ticket-direction-button'>
                                                        <button onClick={() => openInGoogleMaps(eventdetails1?.clubAddress)} title='Get Direction'>
                                                            <FaDirections color='white' />
                                                        </button>
                                                    </div>
                                                )}
                                            </p>
                                            <p>
                                                <SlCalender />
                                                {eventdetails1?.event_date} | {eventdetails1?.event_time}
                                            </p>
                                        </div>
                                    </div>

                                </div>
                            )}



                            <div className="choose-ticket-list">
                                {eventdetailsTicket.map((ticket) => (
                                    <div
                                        key={ticket.id}
                                        className="choose-ticket-card"
                                    >
                                        <div className="ticket-info">
                                            <p className="ticket-name">{ticket.name}</p>
                                            <p className="ticket-price">
                                                {parseFloat(ticket.price) === 0
                                                    ? ""
                                                    : formatIndianCurrency(ticket.price)
                                                }
                                            </p>
                                            <p
                                                className="ticket-description"
                                                dangerouslySetInnerHTML={{ __html: ticket.des }}
                                            />
                                        </div>

                                        <div className="ticket-counter">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const currentCount = ticketCounts[ticket.id] || 0;
                                                    if (currentCount > 0) {
                                                        handleTicketCountChange(ticket.id, currentCount - 1);
                                                    }
                                                }}
                                                disabled={!ticketCounts[ticket.id]}
                                                className="counter-btn minus counter-btn-left"
                                            >
                                                -
                                            </button>

                                            <span className="counter-value">{ticketCounts[ticket.id] || 0}</span>

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    const currentCount = ticketCounts[ticket.id] || 0;
                                                    handleTicketCountChange(ticket.id, currentCount + 1);
                                                }}
                                                disabled={isFreeTiket && calculations.totalQty >= maxFreeTickets}
                                                className="counter-btn plus counter-btn-right"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {isFreeTiket && (
                                <span className='choose-ticket-limit-info'>
                                    * You can buy up to {maxFreeTickets} tickets.
                                </span>
                            )}

                            {/* {calculations.totalQty > 0 && ( */}
                            <div className="event-s-choose-ticket-summary">
                                <div className='event-s-choose-ticket-summary-wrapper'>
                                    <div className="event-s-summary-info">
                                        <span className="event-s-total-tickets">
                                            {calculations.totalQty} {calculations.totalQty === 1 ? 'Ticket' : 'Tickets'} Selected
                                        </span>
                                        <span className="event-s-total-amount">
                                            {isFreeTiket ? calculations.totalQty > 0 ? "₹59" : "₹0" : formatIndianCurrency(calculations.ticketTotal)}
                                        </span>
                                    </div>

                                    <button
                                        onClick={handleProceedToCheckout}
                                        className="event-proceed-book-btn"
                                    >
                                        Proceed to Checkout
                                    </button>
                                </div>
                            </div>
                            {/* )} */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}